import toast from 'react-hot-toast';
import '../../../css/VideoDisplay.css';
import { VideoInfoCard } from './VideoInfoCard';
import { useUser } from '../../../hooks/useUser';
import { useEffect, useMemo, useState } from 'react';
import { EditVideo } from '../../../forms/EditVideoForm';
import { Video } from '../../../interfaces/userInterfaces';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePublicData } from '../../../hooks/usePublicData';
import { deleteUserVideo, updateViews } from '../../../services/fetch-utils';

export const VideoDisplay = (video: Video) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUserVideoListAfterAction } = useUser();
  const { latestVideos, dispatch } = usePublicData();

  const mediaPage = new RegExp('^/media/[0-9]*$');
  const isMediaPage = mediaPage.test(location.pathname);

  const [isMyvideo, setIsMyVideo] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [userData, setUserData] = useState<Video>(video);

  // On media page, pull freshest data from router state
  useEffect(() => {
    if (isMediaPage && location.state?.video) {
      setUserData(location.state.video);
    } else {
      setUserData(video);
    }
  }, [video, isMediaPage, location.state]);

  // Determine ownership without reading localStorage (already in context)
  useEffect(() => {
    if (user?.username && video.username && user.username === video.username && !isMediaPage) {
      setIsMyVideo(true);
    }
  }, [user, video.username, isMediaPage]);

  // ── Suggested videos: everything except this one, up to 10 ────────────────
  const suggestedVideos = useMemo(
    () => latestVideos.filter((v) => v.video_id !== userData.video_id).slice(0, 10),
    [latestVideos, userData.video_id]
  );

  // ── Delete handlers ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!user?.user_id || !isMyvideo) return;
    const result = await deleteUserVideo(user.user_id, video.video_name, video.video_id);
    if (result.status === 204) {
      updateUserVideoListAfterAction(user.user_id);
      toast.success('Video deleted.');
    } else {
      toast.error('Could not delete video. Please try again.');
    }
  };

  const verifyDelete = () => {
    toast(
      (t) => (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <b>Delete this video? This cannot be undone.</b>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{
                padding: '6px 12px',
                background: '#f87171',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
              onClick={() => {
                handleDelete();
                toast.dismiss(t.id);
              }}
            >
              Yes, delete
            </button>
            <button
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid #555',
                color: '#ccc',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
              }}
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { duration: 8000 }
    );
  };

  // ── Feed card (home / profile — not on media page) ─────────────────────────
  if (!isMediaPage) {
    return (
      <div className="latest-feed-grid">
        <video
          className="user-videos-public"
          preload="metadata"
          onClick={() => {
            updateViews(userData.video_id);
            navigate(`/media/${userData.video_id}`, { state: { video } });
          }}
          onLoadedData={() => dispatch({ type: 'increment' })}
          src={userData.video}
          style={{ cursor: 'pointer' }}
        />
        <VideoInfoCard {...userData} />
        {isMyvideo && (
          <div id="user-video-control-panel">
            <button onClick={verifyDelete}>Delete video</button>
            <button onClick={() => setShowEditForm(true)}>Edit video</button>
          </div>
        )}
        {showEditForm && (
          <EditVideo key={new Date().toString()} video={video} setShowEditForm={setShowEditForm} />
        )}
      </div>
    );
  }

  // ── Full media page layout ─────────────────────────────────────────────────
  return (
    <div className="media-page">
      {/* ── Main column ── */}
      <div className="media-main">
        {/* Video */}
        <video
          className="media-video-player"
          controls
          controlsList="nodownload"
          preload="auto"
          onLoadedData={() => dispatch({ type: 'increment' })}
          src={userData.video}
        />

        {/* Info card */}
        <VideoInfoCard {...userData} />

        {/* Owner controls */}
        {isMyvideo && (
          <div id="user-video-control-panel">
            <button id="delete-my-video-buton" onClick={verifyDelete}>
              Delete video
            </button>
            <button id="edit-my-video-buton" onClick={() => setShowEditForm(true)}>
              Edit video
            </button>
          </div>
        )}
        {showEditForm && (
          <EditVideo key={new Date().toString()} video={video} setShowEditForm={setShowEditForm} />
        )}

        {/* Comments */}
        <CommentsSection videoId={userData.video_id} />
      </div>

      {/* ── Up Next sidebar ── */}
      <aside className="media-sidebar">
        <p className="sidebar-label">Up next</p>
        {suggestedVideos.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No other videos yet.</p>
        ) : (
          suggestedVideos.map((v) => (
            <div
              key={v.video_id}
              className="suggested-video-card"
              onClick={() => {
                updateViews(v.video_id);
                navigate(`/media/${v.video_id}`, { state: { video: v } });
              }}
            >
              <div className="suggested-thumb-placeholder">
                <video
                  src={v.video + '#t=0.1'}
                  preload="metadata"
                  muted
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-sm)',
                    display: 'block',
                  }}
                />
              </div>
              <div className="suggested-info">
                <span className="suggested-title">{v.title}</span>
                <span className="suggested-meta">{v.username}</span>
                <span className="suggested-meta">{v.views?.toLocaleString()} views</span>
              </div>
            </div>
          ))
        )}
      </aside>
    </div>
  );
};

// ── Comments section ───────────────────────────────────────────────────────────
// Scaffold — ready to wire to a real comments table when you add one to Supabase.

import { useRef } from 'react';
import { useUser as useUserComments } from '../../../hooks/useUser';

const CommentsSection = ({ videoId }: { videoId: number }) => {
  const { user } = useUserComments();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Placeholder — swap this useState for a real fetch once you have a
  // video_comments table (similar to battle_chat):
  //   id, video_id, user_id, username, avatar_url, text, created_at
  const [comments, setComments] = useState<
    { id: number; username: string; avatar_url: string | null; text: string }[]
  >([]);

  const handleSubmit = () => {
    const text = textareaRef.current?.value.trim();
    if (!text || !user) return;
    // TODO: replace with Supabase insert once table exists
    setComments((prev) => [
      ...prev,
      { id: Date.now(), username: user.username, avatar_url: user.avatar_url ?? null, text },
    ]);
    if (textareaRef.current) textareaRef.current.value = '';
  };

  return (
    <div className="comments-section">
      <p className="comments-header">
        {comments.length} Comment{comments.length !== 1 ? 's' : ''}
      </p>

      {user ? (
        <div className="comment-input-row">
          <textarea ref={textareaRef} placeholder="Add a comment…" />
          <button className="comment-submit-btn" onClick={handleSubmit}>
            Post
          </button>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Log in to leave a comment.</p>
      )}

      <div className="comments-list">
        {comments.length === 0 ? (
          <p className="comment-empty">No comments yet. Be the first.</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="comment-item">
              {c.avatar_url ? (
                <img src={c.avatar_url} alt={c.username} className="comment-avatar" />
              ) : (
                <div className="comment-avatar-placeholder">{c.username[0]?.toUpperCase()}</div>
              )}
              <div className="comment-body">
                <p className="comment-username">{c.username}</p>
                <p className="comment-text">{c.text}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
